// TODO: i18n — see Auth.js for pattern, replace hardcoded strings with t('namespace.key')
import { useState, useEffect } from 'react'
import { supabase } from '../supabase'
import {
  isPushSupported, getPushPermission,
  subscribeToPush, unsubscribeFromPush, getCurrentPushSubscription,
} from '../notifications'

const C = {
  bg: '#F2EDE4', card: '#FDFAF6', border: '#DDD4C6',
  amber: '#B8721F', amberLight: '#D4902A', brown: '#2C1D14',
  muted: '#9A7B68', success: '#2E6B4A', danger: '#B03020',
  successBg: '#EBF5EF', dangerBg: '#FDECEA', warningBg: '#FBF3E8',
}

const S = {
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(44,29,20,0.4)',
    zIndex: 1000, fontFamily: 'Georgia, serif',
  },
  panel: {
    position: 'fixed', top: 0, right: 0, bottom: 0,
    width: '100%', maxWidth: '480px',
    background: C.card, boxShadow: '-8px 0 40px rgba(44,29,20,0.15)',
    borderLeft: `1px solid ${C.border}`,
    overflowY: 'auto', zIndex: 1001,
  },
  header: {
    padding: '24px 28px', display: 'flex',
    justifyContent: 'space-between', alignItems: 'center',
    borderBottom: `1px solid ${C.border}`,
    position: 'sticky', top: 0, background: C.card, zIndex: 10,
  },
  title: { fontSize: '20px', fontWeight: '700', color: C.brown },
  closeBtn: {
    background: 'none', border: 'none', fontSize: '22px',
    cursor: 'pointer', color: C.muted, padding: '0', lineHeight: 1,
  },
  body: { padding: '24px 28px' },
  avatarSection: {
    display: 'flex', alignItems: 'center', gap: '16px',
    marginBottom: '28px', padding: '20px',
    background: C.bg, borderRadius: '12px', border: `1px solid ${C.border}`,
  },
  avatar: {
    width: '56px', height: '56px', borderRadius: '50%',
    background: `linear-gradient(135deg, ${C.amber}, ${C.amberLight})`,
    color: C.card, display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '20px', fontWeight: '700', fontFamily: 'Calibri, sans-serif',
    flexShrink: 0,
  },
  avatarInfo: { flex: 1 },
  avatarName: { fontSize: '16px', fontWeight: '700', color: C.brown, marginBottom: '2px' },
  avatarEmail: { fontSize: '13px', color: C.muted, fontFamily: 'Calibri, sans-serif' },
  sectionTitle: {
    fontSize: '11px', color: C.muted, letterSpacing: '2px',
    textTransform: 'uppercase', marginBottom: '12px',
    fontFamily: 'Calibri, sans-serif',
  },
  divider: { borderTop: `1px solid ${C.border}`, margin: '24px 0' },
  label: {
    fontSize: '10px', color: C.muted, letterSpacing: '1.5px',
    textTransform: 'uppercase', marginBottom: '5px', display: 'block',
    fontFamily: 'Calibri, sans-serif',
  },
  input: (focused) => ({
    width: '100%', background: C.bg,
    border: `1px solid ${focused ? C.amber : C.border}`,
    borderRadius: '8px', padding: '11px 14px', fontSize: '16px',
    fontFamily: 'Georgia, serif', color: C.brown, boxSizing: 'border-box',
    outline: 'none', marginBottom: '14px', transition: 'border-color 0.2s',
    WebkitUserSelect: 'text', userSelect: 'text', touchAction: 'manipulation',
    WebkitAppearance: 'none',
  }),
  select: (focused) => ({
    width: '100%', background: C.bg,
    border: `1px solid ${focused ? C.amber : C.border}`,
    borderRadius: '8px', padding: '11px 14px', fontSize: '16px',
    fontFamily: 'Georgia, serif', color: C.brown, boxSizing: 'border-box',
    outline: 'none', marginBottom: '14px', cursor: 'pointer',
    appearance: 'none', WebkitAppearance: 'none', touchAction: 'manipulation',
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%239A7B68' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat', backgroundPosition: 'right 14px center',
    paddingRight: '36px',
  }),
  inputWrap: { position: 'relative' },
  eyeBtn: {
    position: 'absolute', right: '12px', top: '50%',
    transform: 'translateY(-50%)', background: 'none', border: 'none',
    cursor: 'pointer', color: C.muted, fontSize: '16px', padding: '0',
    marginTop: '-7px',
  },
  row: { display: 'flex', gap: '12px' },
  field: { flex: 1 },
  saveBtn: {
    width: '100%', background: `linear-gradient(135deg, ${C.amber}, ${C.amberLight})`,
    border: 'none', borderRadius: '10px', padding: '14px',
    color: C.card, fontSize: '14px', fontWeight: '700',
    letterSpacing: '2px', textTransform: 'uppercase',
    cursor: 'pointer', fontFamily: 'Georgia, serif',
  },
  saveBtnDisabled: { opacity: 0.6, cursor: 'not-allowed' },
  dangerBtn: {
    width: '100%', background: 'transparent',
    border: `1px solid ${C.danger}44`, borderRadius: '10px', padding: '12px',
    color: C.danger, fontSize: '13px', letterSpacing: '1px',
    textTransform: 'uppercase', cursor: 'pointer',
    fontFamily: 'Georgia, serif', marginTop: '10px',
  },
  alert: (type) => ({
    borderRadius: '8px', padding: '10px 14px', marginBottom: '16px',
    fontSize: '13px', fontFamily: 'Calibri, sans-serif',
    background: type === 'error' ? C.dangerBg : C.successBg,
    border: `1px solid ${type === 'error' ? C.danger : C.success}44`,
    color: type === 'error' ? C.danger : C.success,
  }),
}

const propertyTypes = [
  { id: 'single_family', label: 'Single Family Home' },
  { id: 'condo', label: 'Condo / Townhome' },
  { id: 'multi_family', label: 'Multi-Family' },
  { id: 'mobile', label: 'Mobile Home' },
  { id: 'vacation', label: 'Vacation Home' },
  { id: 'other', label: 'Other' },
]

const currentYear = new Date().getFullYear()
const yearOptions = Array.from({ length: currentYear - 1899 }, (_, i) => currentYear - i)

function Toggle({ checked, onChange }) {
  return (
    <div
      onClick={() => onChange(!checked)}
      role="switch"
      aria-checked={checked}
      style={{
        width: '44px', height: '24px', borderRadius: '12px',
        background: checked ? C.amber : C.border,
        position: 'relative', cursor: 'pointer', flexShrink: 0,
        transition: 'background 0.2s',
      }}
    >
      <div style={{
        position: 'absolute', top: '2px',
        left: checked ? '22px' : '2px',
        width: '20px', height: '20px', borderRadius: '50%',
        background: '#FDFAF6', transition: 'left 0.2s',
        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
      }} />
    </div>
  )
}

export default function SettingsPanel({ session, onClose, onProfileUpdated, activePropertyId, initialSection }) {
  // Get userId from supabase auth directly — avoids session prop issues
  const [userId, setUserId] = useState(session?.user?.id ?? null)
  const [userEmail, setUserEmail] = useState(session?.user?.email ?? '')
  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      if (data?.user?.id) setUserId(data.user.id)
      if (data?.user?.email) setUserEmail(data.user.email)
    })
  }, [])

  useEffect(() => {
    if (initialSection) {
      const el = document.getElementById(`settings-section-${initialSection}`)
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [initialSection])

  const [focus, setFocus] = useState('')
  const [profileLoading, setProfileLoading] = useState(false)
  const [passwordLoading, setPasswordLoading] = useState(false)
  const [profileAlert, setProfileAlert] = useState(null)
  const [passwordAlert, setPasswordAlert] = useState(null)
  const [showCurrentPass, setShowCurrentPass] = useState(false)
  const [showNewPass, setShowNewPass] = useState(false)
  const [showConfirmPass, setShowConfirmPass] = useState(false)

  const [profile, setProfile] = useState({
    first_name: '', last_name: '', phone: '', birth_year: '',
    address: '', city: '', state: '', zip: '',
    property_type: '', year_built: '',
    bedrooms: '', bathrooms: '', sqft: '',
  })
  const [exemptions, setExemptions] = useState([])

  const [passwords, setPasswords] = useState({
    current: '', newPass: '', confirm: '',
  })

  const [notifPrefs, setNotifPrefs] = useState({
    email_enabled: true,
    email_days_before: 30,
    email_day_of_week: 4,
    email_send_hour: 9,
    push_enabled: false,
  })
  const [notifLoading, setNotifLoading] = useState(false)
  const [notifAlert, setNotifAlert] = useState(null)
  const [pushStatus, setPushStatus] = useState(null)

  const email = userEmail
  const getInitials = (p) => {
    const first = (p?.first_name || '').trim().charAt(0).toUpperCase()
    const last = (p?.last_name || '').trim().charAt(0).toUpperCase()
    if (first && last) return first + last
    if (first) return first
    return (email || 'U').charAt(0).toUpperCase()
  }
  const initials = getInitials(profile)

  const fullName = [profile.first_name, profile.last_name].filter(Boolean).join(' ') ||
    session?.user?.user_metadata?.full_name || email

  // Load profile on mount
  useEffect(() => {
    if (!userId) return
    const loadProfile = async () => {
      const [{ data: pd }, { data: prop }] = await Promise.all([
        supabase.from('profiles').select('first_name,last_name,phone,birth_year').eq('id', userId).maybeSingle(),
        activePropertyId
          ? supabase.from('properties').select('*').eq('id', activePropertyId).single()
          : { data: null },
      ])
      setProfile({
        first_name: pd?.first_name || '',
        last_name: pd?.last_name || '',
        phone: pd?.phone || '',
        birth_year: pd?.birth_year || '',
        address: prop?.address || '',
        city: prop?.city || '',
        state: prop?.state || '',
        zip: prop?.zip || '',
        property_type: prop?.property_type || '',
        year_built: prop?.year_built || '',
        bedrooms: prop?.bedrooms || '',
        bathrooms: prop?.bathrooms || '',
        sqft: prop?.sqft || '',
      })
    }
    loadProfile()
  }, [userId, activePropertyId])

  useEffect(() => {
    if (!userId) return
    const loadNotifPrefs = async () => {
      const { data } = await supabase
        .from('notification_preferences')
        .select('email_enabled, email_days_before, email_day_of_week, email_send_hour, push_enabled')
        .eq('user_id', userId)
        .single()
      if (data) {
        setNotifPrefs({
          email_enabled: data.email_enabled ?? true,
          email_days_before: data.email_days_before ?? 30,
          email_day_of_week: data.email_day_of_week ?? 4,
          email_send_hour: data.email_send_hour ?? 9,
          push_enabled: data.push_enabled ?? false,
        })
      }
      if (isPushSupported()) {
        const permission = getPushPermission()
        if (permission === 'denied') {
          setPushStatus('denied')
        } else {
          const sub = await getCurrentPushSubscription()
          setPushStatus(sub ? 'active' : 'inactive')
        }
      }
    }
    loadNotifPrefs()
  }, [userId])

  const checkExemptions = (birthYear) => {
    if (!birthYear) return []
    const age = new Date().getFullYear() - parseInt(birthYear)
    const list = []
    if (age >= 65) list.push({ name: 'Senior Homeowner Exemption', savings: '~$70–100/year', url: 'https://www.assessor.ca.gov' })
    if (age >= 62) list.push({ name: 'Senior Property Tax Postponement', savings: 'Defer taxes until sale', url: 'https://www.sco.ca.gov' })
    return list
  }

  const setP = (field) => (e) => {
    setProfile(p => ({ ...p, [field]: e.target.value }))
    setProfileAlert(null)
  }

  const setPass = (field) => (e) => {
    setPasswords(p => ({ ...p, [field]: e.target.value }))
    setPasswordAlert(null)
  }

  const saveProfile = async () => {
    setProfileLoading(true)
    setProfileAlert(null)

    try {
      const { error: pe } = await supabase.from('profiles').upsert({
        id: userId,
        first_name: profile.first_name || null,
        last_name: profile.last_name || null,
        full_name: [profile.first_name, profile.last_name].filter(Boolean).join(' ') || null,
        phone: profile.phone || null,
        birth_year: profile.birth_year ? parseInt(profile.birth_year) : null,
      })
      if (pe) throw pe

      if (activePropertyId) {
        const { error: prope } = await supabase.from('properties').update({
          address: profile.address || null,
          city: profile.city || null,
          state: profile.state || null,
          zip: profile.zip || null,
          property_type: profile.property_type || null,
          year_built: profile.year_built || null,
          bedrooms: profile.bedrooms || null,
          bathrooms: profile.bathrooms || null,
          sqft: profile.sqft ? parseInt(profile.sqft) : null,
        }).eq('id', activePropertyId)
        if (prope) throw prope
      }

      setProfileAlert({ type: 'success', msg: 'Profile saved successfully.' })
      setExemptions(checkExemptions(profile.birth_year))
      if (onProfileUpdated) onProfileUpdated()
    } catch (err) {
      setProfileAlert({ type: 'error', msg: `Error saving: ${err.message}` })
    }

    setProfileLoading(false)
  }

  const savePassword = async () => {
    if (!passwords.current) {
      setPasswordAlert({ type: 'error', msg: 'Please enter your current password.' }); return
    }
    if (!passwords.newPass) {
      setPasswordAlert({ type: 'error', msg: 'Please enter a new password.' }); return
    }
    if (passwords.newPass.length < 8) {
      setPasswordAlert({ type: 'error', msg: 'New password must be at least 8 characters.' }); return
    }
    if (passwords.newPass !== passwords.confirm) {
      setPasswordAlert({ type: 'error', msg: 'New passwords do not match.' }); return
    }

    setPasswordLoading(true)
    setPasswordAlert(null)

    // Re-authenticate with current password first
    const { error: signInError } = await supabase.auth.signInWithPassword({
      email: userEmail,
      password: passwords.current,
    })

    if (signInError) {
      setPasswordAlert({ type: 'error', msg: 'Current password is incorrect.' })
      setPasswordLoading(false)
      return
    }

    // Update password
    const { error } = await supabase.auth.updateUser({ password: passwords.newPass })

    if (error) {
      setPasswordAlert({ type: 'error', msg: `Error updating password: ${error.message}` })
    } else {
      setPasswordAlert({ type: 'success', msg: 'Password updated successfully.' })
      setPasswords({ current: '', newPass: '', confirm: '' })
    }

    setPasswordLoading(false)
  }

  const saveNotifPrefs = async () => {
    setNotifLoading(true)
    setNotifAlert(null)
    const { error } = await supabase
      .from('notification_preferences')
      .upsert({
        user_id: userId,
        email_enabled: notifPrefs.email_enabled,
        email_days_before: notifPrefs.email_days_before,
        email_day_of_week: notifPrefs.email_day_of_week,
        email_send_hour: notifPrefs.email_send_hour,
        push_enabled: notifPrefs.push_enabled,
        updated_at: new Date().toISOString(),
      })
    if (error) {
      setNotifAlert({ type: 'error', msg: 'Error saving preferences.' })
    } else {
      setNotifAlert({ type: 'success', msg: 'Notification preferences saved.' })
    }
    setNotifLoading(false)
  }

  const handlePushToggle = async (enable) => {
    if (!enable) {
      try {
        await unsubscribeFromPush(userId)
        setNotifPrefs(p => ({ ...p, push_enabled: false }))
        setPushStatus('inactive')
      } catch {
        setNotifAlert({ type: 'error', msg: 'Could not disable push notifications.' })
      }
      return
    }
    try {
      const sub = await subscribeToPush(userId)
      if (sub) {
        setNotifPrefs(p => ({ ...p, push_enabled: true }))
        setPushStatus('active')
      } else {
        setNotifAlert({ type: 'error', msg: 'Push notification permission was denied.' })
      }
    } catch (err) {
      setNotifAlert({ type: 'error', msg: err.message || 'Could not enable push notifications.' })
    }
  }

  const handleSignOut = async () => {
    await supabase.auth.signOut()
  }

  // Guard — if session isn't available don't render
  if (!userId) return null

  return (
    <>
      {/* Overlay */}
      <div style={S.overlay} onClick={onClose} />

      {/* Slide-in panel */}
      <div style={S.panel}>
        <div style={S.header}>
          <div style={S.title}>Account & Profile</div>
          <button style={S.closeBtn} onClick={onClose}>×</button>
        </div>

        <div style={S.body}>

          {/* Avatar summary */}
          <div style={S.avatarSection}>
            <div style={S.avatar}>{initials}</div>
            <div style={S.avatarInfo}>
              <div style={S.avatarName}>{fullName}</div>
              <div style={S.avatarEmail}>{email}</div>
            </div>
          </div>

          {/* ── PERSONAL INFO ── */}
          <div id="settings-section-account" style={S.sectionTitle}>Personal Information</div>
          {profileAlert && <div style={S.alert(profileAlert.type)}>{profileAlert.msg}</div>}

          <div style={S.row}>
            <div style={S.field}>
              <label style={S.label}>First Name</label>
              <input
                style={S.input(focus === 'first_name')}
                value={profile.first_name}
                onChange={setP('first_name')}
                onFocus={() => setFocus('first_name')}
                onBlur={() => setFocus('')}
                placeholder="First name"
                autoComplete="off"
              />
            </div>
            <div style={S.field}>
              <label style={S.label}>Last Name</label>
              <input
                style={S.input(focus === 'last_name')}
                value={profile.last_name}
                onChange={setP('last_name')}
                onFocus={() => setFocus('last_name')}
                onBlur={() => setFocus('')}
                placeholder="Last name"
                autoComplete="off"
              />
            </div>
          </div>

          <label style={S.label}>Phone Number</label>
          <input
            style={S.input(focus === 'phone')}
            value={profile.phone}
            onChange={setP('phone')}
            onFocus={() => setFocus('phone')}
            onBlur={() => setFocus('')}
            placeholder="Phone number"
            type="tel"
            autoComplete="off"
          />

          <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '5px' }}>
            <label style={{ ...S.label, marginBottom: 0 }}>Birth Year</label>
            <span style={{ fontSize: '11px', color: C.muted, fontFamily: 'Calibri, sans-serif' }}>Optional — used to check property tax exemption eligibility</span>
          </div>
          <select
            style={S.select(focus === 'birth_year')}
            value={profile.birth_year}
            onChange={setP('birth_year')}
            onFocus={() => setFocus('birth_year')}
            onBlur={() => setFocus('')}
          >
            <option value="">Prefer not to say</option>
            {Array.from({ length: 100 }, (_, i) => new Date().getFullYear() - 18 - i).map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>

          {exemptions.length > 0 && (
            <div style={{ background: '#FFF8E7', border: `1px solid ${C.amber}`, borderRadius: '8px', padding: '12px', marginBottom: '14px' }}>
              <div style={{ fontSize: '13px', color: C.amber, fontWeight: '600', fontFamily: 'Calibri, sans-serif', marginBottom: '6px' }}>
                You may qualify for property tax exemptions
              </div>
              {exemptions.map(e => (
                <div key={e.name} style={{ fontSize: '12px', color: C.brown, fontFamily: 'Calibri, sans-serif', marginBottom: '4px' }}>
                  • {e.name} — {e.savings}{' '}
                  <a href={e.url} target="_blank" rel="noopener noreferrer" style={{ color: C.amber }}>Learn more →</a>
                </div>
              ))}
            </div>
          )}

          {/* ── EMAIL ── */}
          <div style={S.divider} />
          <div style={S.sectionTitle}>Email</div>
          <input
            style={{ ...S.input(false), opacity: 0.65, cursor: 'not-allowed' }}
            value={email}
            readOnly
            disabled
          />

          <button
            style={{ ...S.saveBtn, ...(profileLoading ? S.saveBtnDisabled : {}) }}
            onClick={saveProfile}
            disabled={profileLoading}
          >
            {profileLoading ? 'Saving...' : 'Save'}
          </button>

          <div style={S.divider} />

          {/* ── NOTIFICATIONS ── */}
          <div id="settings-section-notifications" style={S.sectionTitle}>Notifications</div>
          {notifAlert && <div style={S.alert(notifAlert.type)}>{notifAlert.msg}</div>}

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
            <div>
              <div style={{ fontSize: '14px', fontWeight: '700', color: C.brown, fontFamily: 'Calibri, sans-serif' }}>Email Reminders</div>
              <div style={{ fontSize: '12px', color: C.muted, fontFamily: 'Calibri, sans-serif', marginTop: '2px' }}>Weekly digest of upcoming maintenance</div>
            </div>
            <Toggle checked={notifPrefs.email_enabled} onChange={(v) => setNotifPrefs(p => ({ ...p, email_enabled: v }))} />
          </div>

          {notifPrefs.email_enabled && (
            <>
              <label style={S.label}>Include tasks due within</label>
              <select
                style={S.select(focus === 'email_days_before')}
                value={notifPrefs.email_days_before}
                onChange={e => setNotifPrefs(p => ({ ...p, email_days_before: parseInt(e.target.value) }))}
                onFocus={() => setFocus('email_days_before')}
                onBlur={() => setFocus('')}
              >
                <option value={7}>7 days</option>
                <option value={14}>14 days</option>
                <option value={30}>30 days (recommended)</option>
                <option value={60}>60 days</option>
              </select>

              <label style={S.label}>Send day</label>
              <select
                style={S.select(focus === 'email_day_of_week')}
                value={notifPrefs.email_day_of_week}
                onChange={e => setNotifPrefs(p => ({ ...p, email_day_of_week: parseInt(e.target.value) }))}
                onFocus={() => setFocus('email_day_of_week')}
                onBlur={() => setFocus('')}
              >
                {[['Sunday',0],['Monday',1],['Tuesday',2],['Wednesday',3],['Thursday',4],['Friday',5],['Saturday',6]].map(([label, val]) => (
                  <option key={val} value={val}>{label}</option>
                ))}
              </select>

              <label style={S.label}>Send time (Pacific)</label>
              <select
                style={S.select(focus === 'email_send_hour')}
                value={notifPrefs.email_send_hour}
                onChange={e => setNotifPrefs(p => ({ ...p, email_send_hour: parseInt(e.target.value) }))}
                onFocus={() => setFocus('email_send_hour')}
                onBlur={() => setFocus('')}
              >
                {[[6,'6:00 am'],[7,'7:00 am'],[8,'8:00 am'],[9,'9:00 am'],[10,'10:00 am'],[11,'11:00 am'],[12,'12:00 pm']].map(([hour, label]) => (
                  <option key={hour} value={hour}>{label}</option>
                ))}
              </select>
            </>
          )}

          {isPushSupported() && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
              <div>
                <div style={{ fontSize: '14px', fontWeight: '700', color: C.brown, fontFamily: 'Calibri, sans-serif' }}>Push Notifications</div>
                <div style={{ fontSize: '12px', color: C.muted, fontFamily: 'Calibri, sans-serif', marginTop: '2px' }}>
                  {pushStatus === 'denied' ? 'Blocked in browser settings' : 'Browser alerts for overdue tasks'}
                </div>
              </div>
              {pushStatus === 'denied'
                ? <div style={{ fontSize: '11px', color: C.danger, fontFamily: 'Calibri, sans-serif' }}>Blocked</div>
                : <Toggle checked={notifPrefs.push_enabled} onChange={handlePushToggle} />
              }
            </div>
          )}

          <button
            style={{ ...S.saveBtn, ...(notifLoading ? S.saveBtnDisabled : {}) }}
            onClick={saveNotifPrefs}
            disabled={notifLoading}
          >
            {notifLoading ? 'Saving...' : 'Save Notification Settings'}
          </button>

          <div style={S.divider} />

          {/* ── CHANGE PASSWORD ── */}
          <div style={S.sectionTitle}>Change Password</div>
          {passwordAlert && <div style={S.alert(passwordAlert.type)}>{passwordAlert.msg}</div>}

          <label style={S.label}>Current Password</label>
          <div style={S.inputWrap}>
            <input
              style={{ ...S.input(focus === 'current'), paddingRight: '40px' }}
              type={showCurrentPass ? 'text' : 'password'}
              value={passwords.current}
              onChange={setPass('current')}
              onFocus={() => setFocus('current')}
              onBlur={() => setFocus('')}
              placeholder="Your current password"
              autoComplete="current-password"
            />
            <button style={S.eyeBtn} onClick={() => setShowCurrentPass(!showCurrentPass)}>
              {showCurrentPass ? '🙈' : '👁️'}
            </button>
          </div>

          <label style={S.label}>New Password</label>
          <div style={S.inputWrap}>
            <input
              style={{ ...S.input(focus === 'newPass'), paddingRight: '40px' }}
              type={showNewPass ? 'text' : 'password'}
              value={passwords.newPass}
              onChange={setPass('newPass')}
              onFocus={() => setFocus('newPass')}
              onBlur={() => setFocus('')}
              placeholder="New password (8+ characters)"
              autoComplete="new-password"
            />
            <button style={S.eyeBtn} onClick={() => setShowNewPass(!showNewPass)}>
              {showNewPass ? '🙈' : '👁️'}
            </button>
          </div>

          <label style={S.label}>Confirm New Password</label>
          <div style={S.inputWrap}>
            <input
              style={{
                ...S.input(focus === 'confirm'),
                paddingRight: '40px',
                borderColor: passwords.confirm && passwords.newPass !== passwords.confirm
                  ? C.danger : focus === 'confirm' ? C.amber : C.border,
              }}
              type={showConfirmPass ? 'text' : 'password'}
              value={passwords.confirm}
              onChange={setPass('confirm')}
              onFocus={() => setFocus('confirm')}
              onBlur={() => setFocus('')}
              placeholder="Repeat new password"
              autoComplete="new-password"
            />
            <button style={S.eyeBtn} onClick={() => setShowConfirmPass(!showConfirmPass)}>
              {showConfirmPass ? '🙈' : '👁️'}
            </button>
          </div>
          {passwords.confirm && passwords.newPass !== passwords.confirm && (
            <div style={{ fontSize: '11px', color: C.danger, marginTop: '-10px', marginBottom: '14px', fontFamily: 'Calibri, sans-serif' }}>
              Passwords do not match
            </div>
          )}
          {passwords.confirm && passwords.newPass === passwords.confirm && passwords.newPass && (
            <div style={{ fontSize: '11px', color: C.success, marginTop: '-10px', marginBottom: '14px', fontFamily: 'Calibri, sans-serif' }}>
              Passwords match ✓
            </div>
          )}

          <button
            style={{ ...S.saveBtn, ...(passwordLoading ? S.saveBtnDisabled : {}) }}
            onClick={savePassword}
            disabled={passwordLoading}
          >
            {passwordLoading ? 'Updating...' : 'Update Password'}
          </button>

          <div style={S.divider} />

          {/* ── SIGN OUT ── */}
          <button style={S.dangerBtn} onClick={handleSignOut}>
            Sign Out
          </button>

          <div style={{ height: '24px' }} />
        </div>
      </div>
    </>
  )
}